import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import styled, { keyframes, css } from 'styled-components';
import {
  FaUsers,
  FaWifi,
  FaBell,
  FaApple,
  FaGooglePlay,
  FaArrowRight,
  FaBook,
  FaRobot,
} from 'react-icons/fa';
import StructuredData from '../components/SEO/StructuredData';

// Animations
const shimmer = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(40px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const gradientShift = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
`;

// Styled Components
const Container = styled.div`
  min-height: 100vh;
  background: var(--color-cream);
  overflow-x: hidden;

  @media (max-width: 768px) {
    padding-bottom: 80px;
  }
`;

// Hero Section - Dark dramatic backdrop
const Hero = styled.section`
  position: relative;
  min-height: 100vh;
  background: linear-gradient(135deg, #0a1628 0%, #0f1f3a 50%, #1a2d4a 100%);
  display: flex;
  align-items: center;
  overflow: hidden;

  @media (max-width: 1024px) {
    min-height: auto;
  }

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image:
      radial-gradient(circle at 20% 80%, rgba(212, 165, 116, 0.08) 0%, transparent 40%),
      radial-gradient(circle at 80% 20%, rgba(255, 107, 107, 0.06) 0%, transparent 40%);
    pointer-events: none;
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M30 0L30 60M0 30L60 30M15 15L45 45M45 15L15 45' stroke='%23d4a574' stroke-opacity='0.03' stroke-width='1' fill='none'/%3E%3C/svg%3E");
    opacity: 0.5;
    pointer-events: none;
  }
`;

const HeroContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 8rem 2rem 4rem;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;
  position: relative;
  z-index: 2;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    text-align: center;
    gap: 1.5rem;
    padding: 5rem 1.5rem 2rem;
  }

  @media (max-width: 480px) {
    gap: 1rem;
    padding: 4.5rem 1.25rem 1.5rem;
  }
`;

const HeroTextBlock = styled.div`
  animation: ${fadeInUp} 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards;
`;

const Overline = styled.span`
  display: inline-block;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 0.85rem;
  font-weight: 600;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: var(--color-gold);
  margin-bottom: 1.5rem;
  position: relative;

  @media (max-width: 1024px) {
    margin-bottom: 0.75rem;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 0;
    width: 40px;
    height: 2px;
    background: var(--color-gold);

    @media (max-width: 1024px) {
      left: 50%;
      transform: translateX(-50%);
    }
  }
`;

const HeroTitle = styled.h1`
  font-family: 'Outfit', sans-serif;
  font-size: clamp(3rem, 6vw, 5rem);
  font-weight: 700;
  color: white;
  line-height: 1.1;
  margin-bottom: 1.5rem;
  letter-spacing: -0.02em;

  @media (max-width: 1024px) {
    margin-bottom: 1rem;
  }

  span {
    display: block;
    background: linear-gradient(135deg, var(--color-gold) 0%, var(--color-gold-light) 50%, var(--color-gold) 100%);
    background-size: 200% auto;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    animation: ${shimmer} 4s linear infinite;
  }
`;

const HeroSubtitle = styled.p`
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: clamp(1.1rem, 2vw, 1.35rem);
  color: rgba(255, 255, 255, 0.75);
  line-height: 1.7;
  margin-bottom: 2.5rem;
  max-width: 480px;

  @media (max-width: 1024px) {
    margin-left: auto;
    margin-right: auto;
    margin-bottom: 1.5rem;
    font-size: 1rem;
  }
`;

const HeroButtons = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;

  @media (max-width: 1024px) {
    justify-content: center;
  }

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: center;
  }
`;

const StoreButton = styled.a<{ variant: 'apple' | 'google' }>`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.75rem;
  border-radius: 12px;
  text-decoration: none;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-weight: 600;
  font-size: 1rem;
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);

  ${props => props.variant === 'apple' ? css`
    background: white;
    color: #0a1628;

    &:hover {
      transform: translateY(-3px);
      box-shadow: 0 20px 40px rgba(255, 255, 255, 0.15);
    }
  ` : css`
    background: transparent;
    color: white;
    border: 2px solid rgba(255, 255, 255, 0.3);

    &:hover {
      border-color: var(--color-gold);
      color: var(--color-gold);
      transform: translateY(-3px);
    }
  `}

  svg {
    font-size: 1.5rem;
  }

  span {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    line-height: 1.2;
  }

  .small {
    font-size: 0.7rem;
    font-weight: 400;
    opacity: 0.7;
  }

  .main {
    font-weight: 600;
  }
`;

// Live Stats Counter
const LiveStatsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;

  @media (max-width: 1024px) {
    justify-content: center;
  }

  @media (max-width: 480px) {
    flex-direction: column;
    gap: 0.75rem;
  }
`;

const LiveStatBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const PulseDot = styled.span`
  width: 8px;
  height: 8px;
  background: #4ade80;
  border-radius: 50%;
  animation: ${pulse} 2s ease-in-out infinite;
`;

const StatText = styled.span`
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.8);
`;

const StatNumber = styled.span`
  font-weight: 600;
  color: white;
`;

// Phone Mockup Section
const HeroVisual = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  animation: ${scaleIn} 1s cubic-bezier(0.16, 1, 0.3, 1) 0.3s forwards;
  opacity: 0;

  @media (max-width: 1024px) {
    order: -1;
    margin-bottom: 0;
  }
`;

const PhoneWrapper = styled.div`
  position: relative;
`;

const PhoneFrame = styled.div`
  position: relative;
  width: 300px;
  height: 620px;
  background: linear-gradient(145deg, #2a2a2a, #1a1a1a);
  border-radius: 48px;
  padding: 12px;
  box-shadow:
    0 60px 100px rgba(0, 0, 0, 0.5),
    0 0 0 1px rgba(255, 255, 255, 0.1) inset,
    0 0 80px rgba(212, 165, 116, 0.1);

  @media (max-width: 1024px) {
    width: 200px;
    height: 412px;
    border-radius: 36px;
    padding: 8px;
  }

  @media (max-width: 480px) {
    width: 170px;
    height: 350px;
    border-radius: 32px;
    padding: 7px;
  }
`;

const PhoneScreen = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 38px;
  overflow: hidden;
  background: #1a2d4a;

  img, video {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  @media (max-width: 1024px) {
    border-radius: 30px;
  }

  @media (max-width: 480px) {
    border-radius: 26px;
  }
`;

const PhoneNotch = styled.div`
  position: absolute;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  width: 120px;
  height: 32px;
  background: #1a1a1a;
  border-radius: 20px;
  z-index: 10;

  @media (max-width: 1024px) {
    width: 75px;
    height: 20px;
    top: 14px;
  }

  @media (max-width: 480px) {
    width: 65px;
    height: 18px;
    top: 12px;
  }
`;

// Features Section
const FeaturesSection = styled.section`
  padding: 8rem 2rem;
  background: var(--color-cream);
  position: relative;

  @media (max-width: 768px) {
    padding: 5rem 1.5rem;
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  max-width: 700px;
  margin: 0 auto 5rem;
`;

const SectionOverline = styled.span`
  display: inline-block;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: var(--color-coral);
  margin-bottom: 1rem;
`;

const SectionTitle = styled.h2`
  font-family: 'Outfit', sans-serif;
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: 700;
  color: var(--color-midnight);
  margin-bottom: 1rem;
`;

const SectionSubtitle = styled.p`
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 1.15rem;
  color: var(--color-text-light);
  line-height: 1.7;
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2rem;
  max-width: 1200px;
  margin: 0 auto;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const FeatureCard = styled.div<{ delay: number }>`
  background: white;
  border-radius: 20px;
  padding: 2.5rem 2rem;
  box-shadow: 0 4px 20px rgba(10, 22, 40, 0.06);
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  animation: ${fadeInUp} 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards;
  animation-delay: ${props => props.delay}ms;
  opacity: 0;

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 20px 50px rgba(10, 22, 40, 0.12);
  }
`;

const FeatureIcon = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 16px;
  background: linear-gradient(135deg, var(--color-coral) 0%, #ff8a8a 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: white;
  margin-bottom: 1.5rem;
  box-shadow: 0 8px 20px rgba(255, 107, 107, 0.3);
`;

const FeatureTitle = styled.h3`
  font-family: 'Outfit', sans-serif;
  font-size: 1.4rem;
  font-weight: 700;
  color: var(--color-midnight);
  margin-bottom: 0.75rem;
`;

const FeatureDescription = styled.p`
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 1rem;
  color: var(--color-text-light);
  line-height: 1.6;
`;

// About Section
const AboutSection = styled.section`
  padding: 8rem 2rem;
  background: linear-gradient(135deg, #0a1628 0%, #0f1f3a 100%);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    right: -20%;
    width: 600px;
    height: 600px;
    background: radial-gradient(circle, rgba(212, 165, 116, 0.1) 0%, transparent 70%);
    pointer-events: none;
  }

  @media (max-width: 768px) {
    padding: 5rem 1.5rem;
  }
`;

const AboutContent = styled.div`
  max-width: 900px;
  margin: 0 auto;
  text-align: center;
  position: relative;
  z-index: 1;
`;

const AboutTitle = styled.h2`
  font-family: 'Outfit', sans-serif;
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: 700;
  color: white;
  margin-bottom: 1.5rem;
`;

const AboutText = styled.p`
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 1.2rem;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.8;
  margin-bottom: 1.5rem;
`;

const RulesLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--color-gold);
  padding: 1rem 2rem;
  border: 2px solid var(--color-gold);
  border-radius: 12px;
  text-decoration: none;
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  margin-top: 1rem;

  &:hover {
    background: var(--color-gold);
    color: var(--color-midnight);
    transform: translateY(-2px);
  }

  svg {
    transition: transform 0.3s ease;
  }

  &:hover svg {
    transform: translateX(4px);
  }
`;

// CTA Section
const CTASection = styled.section`
  padding: 6rem 2rem;
  background: var(--color-cream);
  text-align: center;

  @media (max-width: 768px) {
    padding: 4rem 1.5rem;
  }
`;

const CTACard = styled.div`
  max-width: 800px;
  margin: 0 auto;
  background: linear-gradient(135deg, var(--color-coral) 0%, #ff8a8a 100%);
  border-radius: 32px;
  padding: 4rem 3rem;
  box-shadow: 0 30px 80px rgba(255, 107, 107, 0.3);

  @media (max-width: 768px) {
    padding: 3rem 2rem;
    border-radius: 24px;
  }
`;

const CTATitle = styled.h2`
  font-family: 'Outfit', sans-serif;
  font-size: clamp(1.8rem, 4vw, 2.5rem);
  font-weight: 700;
  color: white;
  margin-bottom: 1rem;
`;

const CTASubtitle = styled.p`
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 1.15rem;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 2rem;
`;

const CTAButtons = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
`;

const CTAStoreButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.75rem;
  background: white;
  color: var(--color-midnight);
  border-radius: 12px;
  text-decoration: none;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-weight: 600;
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.2);
  }

  svg {
    font-size: 1.4rem;
  }
`;

// Live stats interface
interface LiveStats {
  playersOnline: number;
  gamesInProgress: number;
}

// Main Component
const HomePage: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [liveStats, setLiveStats] = useState<LiveStats | null>(null);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  // Fetch live stats on mount and every 30 seconds
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await fetch('/api/stats/live');
        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            setLiveStats({
              playersOnline: data.playersOnline,
              gamesInProgress: data.gamesInProgress,
            });
          }
        }
      } catch (error) {
        // Silently fail - stats are not critical
        console.log('Failed to fetch live stats:', error);
      }
    };

    fetchStats();
    const interval = setInterval(fetchStats, 30000); // Poll every 30 seconds

    return () => clearInterval(interval);
  }, []);

  const features = [
    {
      icon: <FaUsers />,
      title: 'Multiplayer Modes',
      description: 'Play live matches with friends or enjoy turn-based games at your own pace. Challenge players anywhere.',
    },
    {
      icon: <FaRobot />,
      title: 'Play Against Bots',
      description: 'Practice your skills against AI opponents. Choose from Easy, Medium, or Hard difficulty levels.',
    },
    {
      icon: <FaWifi />,
      title: 'Offline Play',
      description: 'No internet? No problem. Track scores locally and sync when you\'re back online.',
    },
    {
      icon: <FaBell />,
      title: 'Smart Notifications',
      description: 'Never miss your turn. Get instant alerts when it\'s time to play.',
    },
  ];

  return (
    <>
      <StructuredData type="website" path="/" />
      <Container>
        <Hero>
          <HeroContent>
            <HeroTextBlock>
              <Overline>Traditional Iranian Card Game</Overline>
              <HeroTitle>
                Play Pasur<br />
                <span>Anywhere</span>
              </HeroTitle>
              <HeroSubtitle>
                The beloved card game that's brought families together for generations.
                Now beautifully reimagined for your phone with real-time multiplayer.
              </HeroSubtitle>

              {/* Live Stats Counter */}
              {liveStats && (liveStats.playersOnline > 0 || liveStats.gamesInProgress > 0) && (
                <LiveStatsContainer>
                  {liveStats.playersOnline > 0 && (
                    <LiveStatBadge>
                      <PulseDot />
                      <StatText>
                        <StatNumber>{liveStats.playersOnline}</StatNumber> player{liveStats.playersOnline !== 1 ? 's' : ''} online
                      </StatText>
                    </LiveStatBadge>
                  )}
                  {liveStats.gamesInProgress > 0 && (
                    <LiveStatBadge>
                      <PulseDot />
                      <StatText>
                        <StatNumber>{liveStats.gamesInProgress}</StatNumber> game{liveStats.gamesInProgress !== 1 ? 's' : ''} in progress
                      </StatText>
                    </LiveStatBadge>
                  )}
                </LiveStatsContainer>
              )}

              <HeroButtons>
                <StoreButton
                  href="https://apps.apple.com/us/app/pasur-online-play-score/id6476008595"
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="apple"
                >
                  <FaApple />
                  <span>
                    <span className="small">Download on the</span>
                    <span className="main">App Store</span>
                  </span>
                </StoreButton>
                <StoreButton
                  href="https://play.google.com/store/apps/details?id=com.vakilventures.pasur"
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="google"
                >
                  <FaGooglePlay />
                  <span>
                    <span className="small">Get it on</span>
                    <span className="main">Google Play</span>
                  </span>
                </StoreButton>
              </HeroButtons>
            </HeroTextBlock>

            <HeroVisual>
              <PhoneWrapper>
                <PhoneFrame>
                  <PhoneNotch />
                  <PhoneScreen>
                    <video
                      autoPlay
                      loop
                      muted
                      playsInline
                      poster="/images/app-screenshot.png"
                    >
                      <source src="/videos/app-demo.mp4" type="video/mp4" />
                    </video>
                  </PhoneScreen>
                </PhoneFrame>
              </PhoneWrapper>
            </HeroVisual>
          </HeroContent>
        </Hero>

        <FeaturesSection>
          <SectionHeader>
            <SectionOverline>Features</SectionOverline>
            <SectionTitle>Everything You Need</SectionTitle>
            <SectionSubtitle>
              Automatic scoring, beautiful design, and seamless multiplayer.
              Focus on the game, we'll handle the rest.
            </SectionSubtitle>
          </SectionHeader>

          <FeaturesGrid>
            {features.map((feature, index) => (
              <FeatureCard key={feature.title} delay={index * 150}>
                <FeatureIcon>{feature.icon}</FeatureIcon>
                <FeatureTitle>{feature.title}</FeatureTitle>
                <FeatureDescription>{feature.description}</FeatureDescription>
              </FeatureCard>
            ))}
          </FeaturesGrid>
        </FeaturesSection>

        <AboutSection>
          <AboutContent>
            <SectionOverline style={{ color: 'var(--color-gold)' }}>About Pasur</SectionOverline>
            <AboutTitle>A Game of Strategy & Tradition</AboutTitle>
            <AboutText>
              Pasur is a beloved Iranian card game that has brought families and friends together
              for generations. Known for its perfect blend of strategy and luck, it's the ideal
              game for social gatherings and friendly competition.
            </AboutText>
            <AboutText>
              Our app handles all the complex scoring rules automatically, so you can focus
              on what matters most: enjoying the game with the people you love.
            </AboutText>
            <RulesLink to="/rules">
              <FaBook /> Learn the Rules <FaArrowRight />
            </RulesLink>
          </AboutContent>
        </AboutSection>

        <CTASection>
          <CTACard>
            <CTATitle>Ready to Play?</CTATitle>
            <CTASubtitle>
              Join thousands of Pasur players. Download free today.
            </CTASubtitle>
            <CTAButtons>
              <CTAStoreButton
                href="https://apps.apple.com/us/app/pasur-online-play-score/id6476008595"
                target="_blank"
                rel="noopener noreferrer"
              >
                <FaApple /> App Store
              </CTAStoreButton>
              <CTAStoreButton
                href="https://play.google.com/store/apps/details?id=com.vakilventures.pasur"
                target="_blank"
                rel="noopener noreferrer"
              >
                <FaGooglePlay /> Google Play
              </CTAStoreButton>
            </CTAButtons>
          </CTACard>
        </CTASection>
      </Container>
    </>
  );
};

export default HomePage;
